import { Imports, OHIF } from '@gleamer/types';
import { responseDetail } from './errors';

type AuthHeaderSource = Pick<OHIF.UserAuthenticationService, 'getAuthorizationHeader'>;

type ImportBatch = Imports.ImportBatch;
type ImportBatchFile = Imports.ImportBatchFile;
type ImportBatchFilesQuery = Imports.ImportBatchFilesQuery;
type ImportBatchLine = Imports.ImportBatchLine;
type ImportBatchLinesQuery = Imports.ImportBatchLinesQuery;
type ImportBatchesQuery = Imports.ImportBatchesQuery;
type ImportSources = Imports.ImportSources;
type ImportSubmitted = Imports.ImportSubmitted;
type InvalidListLine = Imports.InvalidListLine;
type SubmitListRequest = Imports.SubmitListRequest;
type Page<T> = Imports.Page<T>;

const BASE_URL = '/api/admin/import';
const BATCHES_URL = `${BASE_URL}/batches`;

/** An answer of viewer-api other than 2xx, with what the server said about it when it said something. */
export class ImportApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ImportApiError';
  }
}

/** The error an answer other than 2xx makes, with what viewer-api said ({@link responseDetail}). */
async function failure(response: Response): Promise<ImportApiError> {
  const detail = await responseDetail(response);
  return new ImportApiError(
    response.status,
    detail ? `${response.status}: ${detail}` : `${response.status} : ${response.statusText}`
  );
}

async function getJson<T>(auth: AuthHeaderSource, url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    headers: new Headers(auth.getAuthorizationHeader()),
  });
  if (!response.ok) {
    throw await failure(response);
  }
  return response.json();
}

function pageParams(page = 0, size = 20): URLSearchParams {
  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('size', size.toString());
  return params;
}

/** The import batches, most recently submitted first. */
export function fetchImportBatches(
  auth: AuthHeaderSource,
  { page = 0, size = 20, status }: ImportBatchesQuery = {}
): Promise<Page<ImportBatch>> {
  const params = pageParams(page, size);
  if (status) {
    params.set('status', status);
  }
  return getJson(auth, `${BATCHES_URL}?${params.toString()}`);
}

export function fetchImportBatch(
  auth: AuthHeaderSource,
  batchId: number | string
): Promise<ImportBatch> {
  return getJson(auth, `${BATCHES_URL}/${encodeURIComponent(String(batchId))}`);
}

/** A batch's lines, in list order, optionally only those in some statuses. */
export function fetchImportBatchLines(
  auth: AuthHeaderSource,
  batchId: number | string,
  { page = 0, size = 100, statuses }: ImportBatchLinesQuery = {}
): Promise<Page<ImportBatchLine>> {
  const params = pageParams(page, size);
  statuses?.forEach(status => params.append('status', status));
  return getJson(
    auth,
    `${BATCHES_URL}/${encodeURIComponent(String(batchId))}/lines?${params.toString()}`
  );
}

/** A batch's files, optionally one line's only, or only those in some statuses. */
export function fetchImportBatchFiles(
  auth: AuthHeaderSource,
  batchId: number | string,
  { page = 0, size = 100, line, statuses }: ImportBatchFilesQuery = {}
): Promise<Page<ImportBatchFile>> {
  const params = pageParams(page, size);
  if (line) {
    params.set('line', line);
  }
  statuses?.forEach(status => params.append('status', status));
  return getJson(
    auth,
    `${BATCHES_URL}/${encodeURIComponent(String(batchId))}/files?${params.toString()}`
  );
}

/** The buckets a list may be read from. */
export function fetchImportSources(auth: AuthHeaderSource): Promise<ImportSources> {
  return getJson(auth, `${BATCHES_URL}/sources`);
}

/** A list viewer-api refused: what is wrong with it, and the first lines at fault. */
export class InvalidListError extends ImportApiError {
  constructor(
    message: string,
    readonly invalidLines: InvalidListLine[]
  ) {
    super(400, message);
    this.name = 'InvalidListError';
  }
}

/**
 * Submits an import list as one batch. A list with lines that are not `CLIENT/PATIENT` is refused as a
 * whole, with the lines at fault ({@link InvalidListError}); nothing is imported until it is fixed.
 */
export async function submitImportList(
  auth: AuthHeaderSource,
  { file, fileName, bucket, name }: SubmitListRequest
): Promise<ImportSubmitted> {
  const form = new FormData();
  form.append('file', file, fileName);
  form.append('bucket', bucket);
  if (name) {
    form.append('name', name);
  }
  // No Content-Type: the browser sets the multipart boundary itself.
  const response = await fetch(BATCHES_URL, {
    method: 'POST',
    headers: new Headers(auth.getAuthorizationHeader()),
    body: form,
  });
  if (response.status === 400) {
    const body = await response
      .clone()
      .json()
      .catch(() => null);
    if (Array.isArray(body?.invalidLines)) {
      throw new InvalidListError(body.message ?? 'The list was refused', body.invalidLines);
    }
  }
  if (!response.ok) {
    throw await failure(response);
  }
  return response.json();
}
